<template>
  <Accordion
    @open="openOverallOverview"
  >
    <b-row slot="short" align-v="center">
      <b-col>
        {{ $t("Webportal.CX.Dashboard.Exploration.Google.OverallScoreOverview") }}
      </b-col>
    </b-row>
    <b-row slot="long" v-bind:class="{ spinner: IsLoading }">
      <b-col style="max-height: 220px">
        <DxSelectBox
          class="mb-1"
          :data-source="[
            $t('Webportal.CX.Dashboard.Month'),
            $t('Webportal.CX.Dashboard.Week')
          ]"
          :value.sync="SelectedTimeRange"
        />
        <DxChart
          :data-source="HistoricRatings"
          id="timeSeries"
        >
          <DxCommonSeriesSettings
            type="bar"
            argument-field="Date"
            :bar-padding="0.5"
          />
          <DxSeries value-field="Value" name="Rating" axis="stars" />

          <DxValueAxis
            :show-zero="true"
            :value-margins-enabled="false"
            name="stars"
            position="left"
            :visual-range="[0, 5]"
          />
          <DxMargin :bottom="20" />
          <DxArgumentAxis
            :value-margins-enabled="false"
            :visual-range.sync="HistoricRatingsTimeRange"
            argument-type="datetime"
          >
            <DxLabel :customize-text="formatArgumentAxis" />
          </DxArgumentAxis>
          <DxLegend :visible="false" />
          <DxTooltip :enabled="true" />
        </DxChart>
      </b-col>
    </b-row>
  </Accordion>
</template>

<script>
import {
  DxChart,
  DxSeries,
  DxArgumentAxis,
  DxValueAxis,
  DxCommonSeriesSettings,
  DxMargin,
  DxLegend,
  DxTooltip,
  DxLabel
} from "devextreme-vue/chart";

import moment from "moment";
import DxSelectBox from "devextreme-vue/select-box";

import Accordion from "../Accordion";
import EventBus from "@/services/EventBus.js";
import DashboardService from "@/services/DashboardService";

export default {
  name: "ScoreHistory",
  components: {
    Accordion,

    DxChart,
    DxSeries,
    DxArgumentAxis,
    DxValueAxis,
    DxCommonSeriesSettings,
    DxMargin,
    DxLegend,
    DxTooltip,
    DxSelectBox,
    DxLabel
  },
  props: {
    apiId: Number
  },
  data() {
    return {
      SelectedTimeRange: this.$t("Webportal.CX.Dashboard.Month"),
      IsLoading: false,
      HistoricRatings: [],
      HistoricRatingsTimeRange: []
    };
  },
  watch: {
    SelectedTimeRange: {
      immediate: true,
      handler(val, oldVal) {
        switch (val) {
          case this.$t("Webportal.CX.Dashboard.Week"):
            this.getHistoricRatings(7 * 4);
            return;

          default:
          case this.$t("Webportal.CX.Dashboard.Month"):
            this.getHistoricRatings(0, 12);
            return;
        }
      }
    }
  },
  methods: {
    // On open the chart area and load data
    openOverallOverview() {
      switch (this.SelectedTimeRange) {
        case this.$t("Webportal.CX.Dashboard.Week"):
          this.getHistoricRatings(7 * 4);
          return;

        default:
        case this.$t("Webportal.CX.Dashboard.Month"):
          this.getHistoricRatings(0, 12);
          return;
      }
    },

    formatArgumentAxis({value, valueText}) {
      if (!moment.isDate(value))
        return valueText || value;

      switch (this.SelectedTimeRange) {
        case this.$t("Webportal.CX.Dashboard.Week"):
          return moment(value).format("DD.MM");

        case this.$t("Webportal.CX.Dashboard.Month"):
          return moment(value).format("MM.YY");

        default:
          return valueText || value;
      }
    },

    setDateToBeginOfWeek(date) {
      let weekDay = date.getDay() || 7; // Sunday = 0, adjusted!
      if (weekDay !== 1) date.setHours(-24 * (weekDay - 1));
      return date;
    },

    setDateToEndOfWeek(date) {
      date = this.setDateToBeginOfWeek(date);
      date.setHours(24 * 7);
      return date;
    },

    getHistoricRatings(offsetDays, offsetMonths, offsetYears) {
      if (!this.apiId) return;

      // Round to end of day
      let now = new Date();
      let today = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1,
        0, 0, 0, 0
      );

      let offsetDate = new Date(
        now.getFullYear() - (offsetYears || 0),
        now.getMonth() - (offsetMonths || 0),
        now.getDate() - (offsetDays || 0),
        0, 0, 0, 0
      ); // Round to begin of day

      this.IsLoading = true;
      let baseURL = process.env.BASE_URL + "api/dashboard";

      // Get settings from 'this.$parent.$parent.$parent.$parent'
      EventBus.$emit("GetCX_Filter", currentFilter => {
        DashboardService.getHistorical(
          currentFilter,
          null,       /* cancelToken CX TODO ? */
          offsetDate, /* dateFrom */
          today,      /* dateTo */
          this.apiId  /* apiId */
        ).then(response => {
          // We need to use next tick, as the date would change time range for some reason..
          this.$nextTick(() => {
            // Set time range for chart, it would fit to min and max of data if no data is set
            this.HistoricRatingsTimeRange = [today, offsetDate];
            this.IsLoading = false;
          });

          // No data, no further action
          if (
            _.isEmpty(response?.data?.HistoricValues) &&
            _.isEmpty(response?.data?.PreviousWeek)
          )
            return;

          // Insert element that predates the first element in time range,
          // as it still is valid until the time range begins
          let preWeek = response?.data?.PreviousWeek;
          if (!_.isEmpty(preWeek)) {
            if (_.isEmpty(response.data.HistoricValues))
              response.data.HistoricValues = [];
            response.data.HistoricValues.unshift(...preWeek);
          }

          // Convert data to format understood by the chart
          this.HistoricRatings = _.map(
            response.data.HistoricValues,
            hv => {
              return {
                Value: hv.Value,
                Date: new Date(hv.Date)
              };
            }
          );

          // Last value persists at least until today
          if (!_.isEmpty(this.HistoricRatings.length)) {
            _.last(this.HistoricRatings).Date = today;
          }
        });
      });
    },
  }
};
</script>

<style scoped>
#timeSeries {
  height: 200px;
}
</style>
